import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { copyText } from '../../lib/platform';
import { Button } from '../../ui/Button';
import { TextField, Toggle } from '../../ui/Field';
import { ListGroup } from '../../ui/List';
import { Page } from '../../ui/Page';
import { toast } from '../../ui/Toast';
import { type Friend, useFriends } from './friends';

/**
 * Friends: each person keeps their own app and account; a friend sees only what I turn on for
 * them, and I choose whose flights appear on my map.
 */
export function FriendsPage() {
  const [friends, reload] = useFriends();
  const [invite, setInvite] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  // Opened from an invite link: fill it in.
  useEffect(() => {
    const join = new URLSearchParams(location.search).get('join');
    if (join) setInvite(`${location.origin}/settings/friends?join=${join}`);
  }, []);

  const makeInvite = async () => {
    setBusy(true);
    try {
      const r = await api<{ link: string }>('/api/friends/invite', { method: 'POST', body: {} });
      setLink(r.link);
      const shared =
        typeof navigator.share === 'function'
          ? await navigator
              .share({ title: 'Be my friend on Trips App', url: r.link })
              .then(() => true)
              .catch(() => false)
          : false;
      if (!shared) toast((await copyText(r.link)) ? 'Invite link copied' : 'Copy the link below');
      reload();
    } catch {
      toast("Couldn't make an invite. Check the connection.", { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api<{ name: string }>('/api/friends/accept', {
        method: 'POST',
        body: { invite },
      });
      toast(`You and ${r.name} are friends`);
      setInvite('');
      history.replaceState(history.state, '', location.pathname + location.hash);
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't accept that invite.", { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const set = async (
    f: Friend,
    change: Partial<Pick<Friend, 'shareMap' | 'shareStats' | 'showOnMap'>>,
  ) => {
    await api(`/api/friends/${encodeURIComponent(f.id)}`, { method: 'PATCH', body: change }).catch(
      () => toast("Couldn't save that. Check the connection.", { tone: 'error' }),
    );
    reload();
  };

  const remove = async (f: Friend) => {
    if (!confirm(`Remove ${f.name ?? 'this friend'}? You'll stop seeing each other's flights.`))
      return;
    await api(`/api/friends/${encodeURIComponent(f.id)}`, { method: 'DELETE' }).catch(() => {});
    reload();
  };

  const linked = friends.filter((f) => f.status === 'linked');
  const pending = friends.filter((f) => f.status === 'pending');

  return (
    <Page title="Friends" backTo="/settings">
      {linked.map((f) => (
        <ListGroup key={f.id} title={f.name ?? 'Friend'}>
          <Toggle
            label="Show on my Flight Map"
            checked={f.showOnMap}
            onChange={(v) => void set(f, { showOnMap: v })}
          />
          <Toggle
            label="Share my flight map"
            detail="Routes and airports only"
            checked={f.shareMap}
            onChange={(v) => void set(f, { shareMap: v })}
          />
          <Toggle
            label="Share my flight stats"
            detail="Airlines, aircraft, seats, delays and the rest"
            checked={f.shareStats}
            onChange={(v) => void set(f, { shareStats: v })}
          />
          <Button variant="ghost" icon="trash" onClick={() => void remove(f)}>
            Remove friend
          </Button>
        </ListGroup>
      ))}

      <ListGroup
        title="Add a friend"
        footer="Each of you keeps your own account. Friends see only your flight map and stats, and only what you switch on."
      >
        <Button
          variant="primary"
          icon="user-plus"
          disabled={busy}
          onClick={() => void makeInvite()}
        >
          Invite a friend
        </Button>
        {link && (
          <p className="t-footnote muted" style={{ wordBreak: 'break-all' }}>
            {link}
          </p>
        )}
        {pending.length > 0 && (
          <p className="t-footnote muted">
            {pending.length === 1 ? 'One invite is' : `${pending.length} invites are`} waiting to be
            accepted (good for 7 days).
          </p>
        )}
        <TextField
          label="Got an invite? Paste it here"
          value={invite}
          onChange={setInvite}
          placeholder="https://tripsapp.app/settings/friends?join=…"
        />
        <Button
          variant="line"
          disabled={busy || invite.trim().length < 20}
          onClick={() => void accept()}
        >
          Accept invite
        </Button>
      </ListGroup>
    </Page>
  );
}
